import { QUEST_LABELS, RESOURCE_LABELS } from '@bienenkampf/shared';
import { Badge, Button, Card } from '@bienenkampf/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatNumber } from '../lib/time.js';
import { useTRPC } from '../lib/trpc.js';

/** Startquests (Konzept 11): fuehren durch die erste halbe Stunde, jede mit kleiner Belohnung. */
export function QuestCard({ worldId, hiveId }: { worldId: number; hiveId: number }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const quests = useQuery(trpc.quest.list.queryOptions({ worldId }));
  const claim = useMutation(
    trpc.quest.claim.mutationOptions({
      onSettled: () => {
        void qc.invalidateQueries({ queryKey: trpc.quest.pathKey() });
        void qc.invalidateQueries({ queryKey: trpc.hive.get.queryKey({ hiveId }) });
      },
    }),
  );
  if (!quests.data || quests.data.open === 0) return null;
  const items = quests.data.items;
  const done = items.filter((i) => i.completedAt).length;
  return (
    <Card
      title="Erste Schritte"
      action={
        <Badge tone={quests.data.claimable > 0 ? 'amber' : 'neutral'}>
          {done}/{items.length}
        </Badge>
      }
    >
      <ol className="flex flex-col gap-2 text-sm">
        {items.map((q, idx) => {
          const meta = QUEST_LABELS[q.key] ?? { title: q.key, hint: '' };
          const rewardText = (['nectar', 'pollen', 'wax'] as const)
            .filter((r) => q.reward[r] > 0)
            .map((r) => `${formatNumber(q.reward[r])} ${RESOURCE_LABELS[r]}`)
            .join(', ');
          const state = q.claimedAt ? 'claimed' : q.completedAt ? 'claimable' : 'open';
          return (
            <li key={q.key} className="flex items-start gap-2">
              <span
                className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full text-[11px] ${state === 'open' ? 'bg-stone-800 text-stone-400' : 'bg-emerald-700 text-white'}`}
              >
                {state === 'open' ? idx + 1 : '✓'}
              </span>
              <div className="min-w-0 flex-1">
                <div className={state === 'claimed' ? 'text-stone-500 line-through' : ''}>
                  {meta.title}
                </div>
                {state === 'open' && <div className="text-xs text-stone-500">{meta.hint}</div>}
                <div className="text-xs text-amber-300/80">Belohnung: {rewardText}</div>
              </div>
              {state === 'claimable' && (
                <Button
                  size="sm"
                  onClick={() => claim.mutate({ worldId, key: q.key, hiveId })}
                  loading={claim.isPending}
                >
                  Abholen
                </Button>
              )}
            </li>
          );
        })}
      </ol>
      {claim.error && <p className="mt-2 text-sm text-red-400">{claim.error.message}</p>}
    </Card>
  );
}
